<script>
import {ref, watch} from "vue";
import useSharedData from "@/use/SharedData.js";
import useTools from "@/use/Tools.js";
import {useSecurityStore} from "@/stores/Security.js"
import useSharedMethod from "@/use/SharedMethod.js";
import {usePaySlipAnnexStore} from "@/stores/PaySlipAnnex.js";
import PaySlipAnnexFilter from "@/views/PaySlipSynthesis/Components/PaySlipAnnexFilter.vue";
import axios from "axios";
import {storeToRefs} from "pinia";

export default {
    name: "PaySlipSynthesis",
    components: {PaySlipAnnexFilter},
    setup() {
        const {
            loading,
            total,
            itemsPerPage,
            filter,
        } = useSharedData();

        const {
            transformFilterValues,
            priceFormat,
            formatedDate,
            formatedMonth,
            exportPdf
        } = useSharedMethod();

        const {$i18n, $router} = useTools();

        const paySlipAnnexStore = usePaySlipAnnexStore();
        const securityStore = useSecurityStore();

        const headers = [
            {
                text: "...",
                sortable: false,
                align: "left",
                value: "action",
                width: "2%"
            },
            {
                text: $i18n.t("common.month"),
                align: "left",
                value: "month",
                sortable: true,
                width: "0%"
            },
            {
                text: $i18n.t("common.name"),
                align: "left",
                value: "name",
                sortable: true,
                width: "0%"
            },
            {
                text: $i18n.t("module.workers"),
                align: "left",
                value: "workerNumber",
                sortable: false,
                width: "0%"
            },
            {
                text: $i18n.t("pay_slip.masse"),
                align: "end",
                sortable: true,
                value: "masse",
            },
            {
                text: $i18n.t("pay_slip.net_to_pay"),
                align: "end",
                sortable: true,
                value: "net",
            },
            {
                text: $i18n.t("common.base_imposable"),
                align: "end",
                sortable: true,
                value: "baseImposable",
            },
            {
                text: $i18n.t("common.base_cotisable"),
                align: "end",
                sortable: true,
                value: "baseCotisable",
            },
            {
                text: $i18n.t("pay_slip.total_earnings"),
                align: "end",
                sortable: true,
                value: "totalGains",
            },
            {
                text: $i18n.t("pay_slip.total_restraint"),
                align: "end",
                sortable: true,
                value: "totalRetenues",
            },
            {
                text: $i18n.t("pay_slip.total_cnas"),
                align: "end",
                sortable: true,
                value: "totalCnas",
            },
            {
                text: $i18n.t('common.step'),
                align: "left",
                sortable: false,
                value: "state",
            },
            {
                text: $i18n.t("common.validation"),
                align: "start",
                sortable: true,
                value: "validationStatus.lastState",
                width: "0%"
            },
            {
                text: $i18n.t("common.validation_date"),
                align: "start",
                sortable: true,
                value: "validationStatus.lastValidationDate",
                width: "0%"
            },
            {
                text: $i18n.t("common.next_validator"),
                align: "start",
                sortable: true,
                value: "validationStatus.nextValidator",
                width: "0%"
            },
        ];

        const actions = [
            {
                title: $i18n.t('common.lock'),
                icon: 'mdi-lock',
                color: 'grey darken-2',
                access: () => securityStore.authorities['SAVE_PAY_SLIP'],
                disabled: () => false,
                func: (item) => {
                    $router.push({name: 'pay_slip_annexes.close', params: {id: item.id}});
                },
            },
            {
                title: $i18n.t("common.delete"),
                icon: 'mdi-trash-can',
                color: 'red',
                access: () => securityStore.authorities['SAVE_PAY_SLIP'],
                disabled: () => false,
                func: (item) => {
                    $router.push({name: 'pay_slip_annexes.delete', params: {id: item.id}})
                },
            },
            {
                title: $i18n.t("module.edms"),
                icon: 'mdi-folder',
                color: 'amber',
                access: () => true,
                disabled: () => false,
                func: (item) => {
                    $router.push({name: 'pay_slip_annexes.documents', params: {id: item.id}})
                },
            },
        ];

        const {itemsToBeValidate} = storeToRefs(securityStore);

        const searchParams = ref({
            sortBy: "month",
            descending: true,
            page: 1,
            itemsPerPage: itemsPerPage
        });

        function handleSearch(params) {
            loading.value = true;
            paySlipAnnexStore.setPaySlipAnnexes([]);
            total.value = 0;

            filter.value.itemsToBeValidate = itemsToBeValidate.value;

            filter.value.page = params.page - 1;
            filter.value.rowsPerPage = params.itemsPerPage;
            filter.value.sortBy = params.sortBy;
            filter.value.descending = params.descending;

            axios.get('/paySlipAnnexes', {params: transformFilterValues(filter.value)}).then((response) => {
                paySlipAnnexStore.setPaySlipAnnexes(response.data.elements);
                total.value = response.data.total;
            }).finally(() => {
                loading.value = false;
            });
        }

        function findAll() {
            handleSearch(searchParams.value)
        }

        watch(() => [filter.value, itemsToBeValidate.value], () => {
            searchParams.page = 1;
            findAll();
        }, {deep: true, immediate: true});

        watch(searchParams, (newParams) => {
            handleSearch(newParams);
        }, {deep: true});

        function clearFilter() {
            Object.keys(filter.value).forEach(key => {
                filter.value[key] = null;
            });
        }

        function view(item) {
            exportPdf(`/printed/getEtatRecapitulatif/${item.id}`)
        }

        return {
            paySlipAnnexStore,
            securityStore,
            headers,
            loading,
            total,
            filter,
            searchParams,
            actions,
            itemsToBeValidate,
            clearFilter,
            findAll,
            priceFormat,
            formatedDate,
            formatedMonth,
            view,
        };
    },
};
</script>

<template>
    <div>
        <w-app
            :title="$t('module.pay_slip_synthesis')"
            show-total
            :total="total"
            show-filter
            :filter="filter"
            @clearFilter="clearFilter"
        >
            <template v-slot:filter>
                <pay-slip-annex-filter v-model="filter"/>
            </template>

            <template v-slot:leftToolbar>
                <v-checkbox
                    v-model="itemsToBeValidate"
                    :label="$t('common.requests_need_validation')"
                    :true-value="true"
                    :false-value="false"
                    class="ml-2"
                    hide-details
                />
            </template>

            <template v-slot:rightToolbar>
                <w-btn-reload :loading="loading" @press="findAll"/>
            </template>

            <template v-slot:content>
                <w-server-data-table
                    v-model="searchParams"
                    :actions="actions"
                    :headers="headers"
                    :items="paySlipAnnexStore.paySlipAnnexes"
                    :loading="loading"
                    :total="total"
                    show-validation
                    root-path="pay_slip_annexes"
                    module="PAY_SLIP_ANNEX"
                    @rowDbClick="view"
                >
                    <template v-slot:[`item.month`]="{ item }">
                        <w-chip :text="formatedMonth(item.month)"/>
                    </template>
                    <template v-slot:[`item.name`]="{ item }">
                        <w-inline-text bold :text="item.name"/>
                    </template>
                    <template v-slot:[`item.workerNumber`]="{ item }">
                        <w-chip outlined :text="item.workerNumber"/>
                    </template>
                    <template v-slot:[`item.masse`]="{ item }">
                        <w-chip color="teal" :text="priceFormat(item.masse)"/>
                    </template>
                    <template v-slot:[`item.net`]="{ item }">
                        <w-inline-text :text="priceFormat(item.net)"/>
                    </template>
                    <template v-slot:[`item.baseImposable`]="{ item }">
                        <w-inline-text :text="priceFormat(item.baseImposable)"/>
                    </template>
                    <template v-slot:[`item.baseCotisable`]="{ item }">
                        <w-inline-text :text="priceFormat(item.baseCotisable)"/>
                    </template>
                    <template v-slot:[`item.totalGains`]="{ item }">
                        <w-inline-text :text="priceFormat(item.totalGains)"/>
                    </template>
                    <template v-slot:[`item.totalRetenues`]="{ item }">
                        <w-inline-text :text="priceFormat(item.totalRetenues)"/>
                    </template>
                    <template v-slot:[`item.totalCnas`]="{ item }">
                        <w-inline-text :text="priceFormat(item.totalCnas)"/>
                    </template>

                    <template v-slot:[`item.state`]="{ item }">
                        <w-chip v-if="item.fermer" color="grey darken-2" :text="$t('common.locked')"/>
                        <w-chip v-else-if="item.archived" color="purple" text="Archivé"/>
                        <w-chip v-else color="blue" text="En Cours"/>
                    </template>
                </w-server-data-table>
            </template>
        </w-app>
        <router-view/>
    </div>
</template>